import { isMobileDevice } from '@cash-web/shared-util-user-agent'

export const getQueryParam = (paramKey: string) => {
  const urlParams = new URLSearchParams(window.location.search)
  const param = urlParams.get(paramKey)

  return param
}
const createAllowedQueryParamValues =
  (...queryStringKey: string[]) =>
  (allowedInternalRedirects?: string[], allowedRedirectPatterns?: RegExp[]) => {
    const redirect = queryStringKey.map(key => getQueryParam(key)).find(Boolean) as string
    if (!redirect) return null
    if (!allowedInternalRedirects && !allowedRedirectPatterns) return redirect

    if (allowedInternalRedirects?.includes(redirect)) {
      return redirect
    } else if (allowedRedirectPatterns?.find(pattern => redirect.match(pattern))) {
      return redirect
    }
    return null
  }

// This is internal to the product for when you attempt to hit a route.
export const getInternalRedirect = () => {
  return getQueryParam('redirect') ?? getQueryParam('return_to')
}

// This is used to redirect to a full URL so can also be used to redirect to “external” URLs.
export const getRedirectTo = () => {
  return getQueryParam('redirect_to')
}

export const createAllowedRedirect = createAllowedQueryParamValues('redirect', 'return_to')
export const createAllowedRedirectTo = createAllowedQueryParamValues('redirect_to')

/**
 * @returns {string} the redirect/return_to query param used by desktop web, to restrict the internal redirects we support.
 */
export const getSupportedInternalRedirect = () => {
  return createAllowedRedirect(
    ['support', 'tax-information', 'individual-tax-information'],
    [
      // All redirects to the account web app are supported.
      /^\/account\/*/,
      // Redirects to the authenticated Trustpilot application (in marketing-apps namespace).
      /^\/reviews\/review-us/,
      // Redirects to the taxes web app.
      /^\/taxes\//,
    ]
  )
}

/**
 * Default login behaviour is not enabled on mobile.
 * For specific scenarios we allow login to happen.
 *
 * @returns {boolean} returns true if the query params specified support mobile login.
 */
export const shouldShowMobileSplash = () => {
  // On mobile web we support logging in to /support, /account/tax-information, and /account/individual-tax-information
  const mobileSupportedRedirect = createAllowedRedirect(
    ['support', 'tax-information', 'individual-tax-information'],
    [
      // Links to the Documents tab - the destination for tax short URLs (ex: cash.app/1099k)
      /^\/account\/documents/,
      // Links to pages where customer can update their business and personal tax information.
      /account\/tax-information/,
      /account\/individual-tax-information/,
      // Links to the authenticated Trustpilot application (in marketing-apps namespace).
      /^\/reviews\/review-us/,
      // Links to the taxes web application.
      /^\/taxes\//,
      // Links to the Cash App Pay grant flow landing page
      /account\/grant-flow/,
    ]
  )
  // On mobile web we supporting logging in and redirecting to cash.app/cash-app/pay/exchange-session
  // TODO: cash.app/cash-app/* URLs should be used as API urls and should not be browsable but customers.
  const mobileSupportedRedirectTo = createAllowedRedirectTo([], [/\/cash-app\/pay\/exchange-session/])
  const hasNoMobileLoginSupportedQueryParam = mobileSupportedRedirect == null && mobileSupportedRedirectTo == null
  // we should only show the mobile splash screen if the current device is mobile
  // and the is no mobile login supported query param
  return isMobileDevice() && hasNoMobileLoginSupportedQueryParam
}
